import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import PTAFormField from 'Components/PTAFormField'
import { WithLoader } from 'Components/WithLoader'
import DeviceDrawer from 'Services/ServiceProviderManagement/Features/Device/Components/DeviceDrawer'
import DeviceList from 'Services/ServiceProviderManagement/Features/Device/Components/DeviceList/DeviceList'
import { useDeleteSingleDeviceMutation } from 'Services/ServiceProviderManagement/Features/Device/deviceApi'
import useDevicesWithParams from 'Services/ServiceProviderManagement/Features/Device/Hooks'
import {
  IS_WALTTI_SERVICE_ENVIRONMENT,
  ROWS_PER_PAGE_DEFAULT,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL
} from 'Util/constants'
import useListFunctions from 'Util/hooks/useListFunctions'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import { Box, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

export const DEVICE_DETAILS_DRAWER_TAB_PARAMETER_KEY = 'deviceDetailsDrawerTab'

export default function DeviceListPage() {
  const baseUrl = `${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/devices`
  const navigate = useNavigate()

  const {
    devices,
    isFetching: isDevicesFetching,
    totalResults,
    searchQueryParams,
    setSearchQueryParams,
    selectedDeviceId,
    setSelectedDeviceId,
    selectedPta,
    setSelectedPta
  } = useDevicesWithParams()

  const [searchParams, setSearchParams] = useSearchParams()
  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedDeviceId)

  const [deleteDevice, { isLoading: isDeleteLoading }] = useDeleteSingleDeviceMutation()

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SERVICE_PROVIDER_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Devices`
    }
  ]

  const {
    searchText,
    handleChangePage,
    handleRequestSort,
    handleSearchTextChange,
    handleSearch,
    handlePTAChange
  } = useListFunctions({
    searchQueryParams,
    setSearchQueryParams
  })

  const handleDrawerClose = () => {
    setSelectedDeviceId('')
    setSelectedPta('')
    setIsDrawerOpen(false)
    searchParams.delete(DEVICE_DETAILS_DRAWER_TAB_PARAMETER_KEY)
    setSearchParams(searchParams)
  }

  const handleRowClick = (id: string, pta: string) => {
    setSelectedDeviceId(id)
    setSelectedPta(pta)
    setIsDrawerOpen(true)
  }

  const handleDelete = () => {
    if (!selectedDeviceId || !selectedPta) return
    deleteDevice({ deviceId: selectedDeviceId, pta: selectedPta })
      .unwrap()
      .then(() => {
        handleDrawerClose()
        SnackbarUtils.success(t`Device successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the device.`)
      })
  }

  const isLoading = isDevicesFetching || isDeleteLoading

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Devices`}</Typography>
        <Box sx={{ mt: 4 }}>
          <ListActions>
            <CustomButton
              onClick={() => navigate(`${baseUrl}/create/`)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new device`}
            </CustomButton>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {IS_WALTTI_SERVICE_ENVIRONMENT && (
                <PTAFormField
                  value={searchQueryParams.pta}
                  onChange={handlePTAChange}
                  required={false}
                  displayEmptyFirstOption={true}
                />
              )}
              <ListSearch
                searchText={searchText}
                handleSearchTextChange={handleSearchTextChange}
                handleSearch={handleSearch}
                placeholder={t`Search by name or code`}
              />
            </Box>
          </ListActions>
          <Box sx={{ pt: 1 }}>
            <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
              <DeviceList
                rows={devices}
                isLoading={isLoading}
                params={searchQueryParams}
                handleRequestSort={handleRequestSort}
                handleChangePage={handleChangePage}
                count={totalResults}
                page={
                  searchQueryParams.start_index
                    ? Number.parseInt(searchQueryParams.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                    : 0
                }
                onRowClick={handleRowClick}
                selectedDeviceId={selectedDeviceId}
              />
            </WithLoader>
          </Box>
        </Box>
        <DeviceDrawer
          open={isDrawerOpen}
          handleClose={handleDrawerClose}
          selectedDeviceId={selectedDeviceId}
          handleDeviceDelete={handleDelete}
        />
      </Box>
    </Box>
  )
}
